// Central data for all IT Training courses
// Used by listing pages AND individual course detail pages

export const allCourses = [

  /* ─── PROGRAMMING COURSES ─── */
  {
    slug: 'python-training',
    category: 'programming',
    title: 'Python Training',
    icon: '🐍',
    duration: '2 Months',
    level: 'Beginner – Advanced',
    fee: 'Contact for fee',
    tagline: 'The world\'s most popular programming language',
    description: 'Python is the go-to language for web development, data science, machine learning, automation, and AI. This course takes you from the very basics to building real-world applications. You will learn Python syntax, object-oriented programming, file handling, exception management, and popular libraries like NumPy, Pandas, and Flask.',
    highlights: [
      'Core Python: variables, loops, functions, OOP',
      'File handling, exception handling, modules',
      'Introduction to NumPy, Pandas, and Matplotlib',
      'Web scraping with BeautifulSoup',
      'Mini project: Build a data analysis tool',
      'Certificate of completion + placement support',
    ],
    curriculum: [
      { week: 'Week 1–2', topic: 'Python Basics', items: ['Variables & Data Types', 'Control Flow', 'Functions', 'Lists, Tuples, Dicts'] },
      { week: 'Week 3–4', topic: 'Object-Oriented Python', items: ['Classes & Objects', 'Inheritance', 'Exception Handling', 'File I/O'] },
      { week: 'Week 5–6', topic: 'Libraries & Frameworks', items: ['NumPy & Pandas', 'Matplotlib Basics', 'Flask Web Framework', 'REST APIs with Flask'] },
      { week: 'Week 7–8', topic: 'Project & Placement', items: ['Capstone Project', 'Resume Preparation', 'Mock Interviews', 'Job Placement Support'] },
    ],
    career: ['Python Developer', 'Data Analyst', 'Backend Developer', 'Automation Engineer'],
  },
  {
    slug: 'python-ai-training',
    category: 'programming',
    title: 'Python With AI Training',
    icon: '🤖',
    duration: '3 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Build intelligent applications with Python and AI',
    description: 'This advanced course combines Python programming with Artificial Intelligence concepts. You will learn how to build AI-powered applications using libraries like TensorFlow and Keras, work with neural networks, and implement real-world AI use cases such as image recognition, chatbots, and NLP.',
    highlights: [
      'AI fundamentals and problem-solving approaches',
      'Python for AI: NumPy, Pandas, Scikit-learn',
      'Neural networks with TensorFlow and Keras',
      'Natural Language Processing (NLP)',
      'Image classification and computer vision',
      'Live AI project + placement support',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Python & AI Foundations', items: ['Python Refresher', 'AI/ML Concepts', 'NumPy & Pandas', 'Data Preprocessing'] },
      { week: 'Month 2', topic: 'Machine Learning & Deep Learning', items: ['Supervised Learning', 'Neural Networks', 'TensorFlow & Keras', 'Model Training'] },
      { week: 'Month 3', topic: 'Advanced AI & Projects', items: ['NLP with NLTK', 'Computer Vision', 'Chatbot Development', 'Capstone AI Project'] },
    ],
    career: ['AI Engineer', 'ML Developer', 'NLP Engineer', 'Data Scientist'],
  },
  {
    slug: 'python-ml-training',
    category: 'programming',
    title: 'Python With ML Training',
    icon: '📊',
    duration: '3 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Master Machine Learning algorithms with Python',
    description: 'Dive deep into Machine Learning with Python. This course covers supervised and unsupervised learning algorithms, feature engineering, model evaluation, and deployment. You will use Scikit-learn, Pandas, and Matplotlib to build and evaluate ML models from scratch.',
    highlights: [
      'Supervised learning: regression, classification, SVM',
      'Unsupervised learning: clustering, PCA',
      'Feature engineering and data preprocessing',
      'Model evaluation and cross-validation',
      'Scikit-learn pipeline development',
      'End-to-end ML project',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Data & ML Foundations', items: ['Python for ML', 'Exploratory Data Analysis', 'Data Cleaning', 'Feature Engineering'] },
      { week: 'Month 2', topic: 'Core ML Algorithms', items: ['Linear & Logistic Regression', 'Decision Trees', 'SVM & KNN', 'Random Forest'] },
      { week: 'Month 3', topic: 'Advanced ML & Deployment', items: ['Unsupervised Learning', 'Model Evaluation', 'ML Pipelines', 'Project & Placement'] },
    ],
    career: ['ML Engineer', 'Data Scientist', 'AI Developer', 'Research Analyst'],
  },
  {
    slug: 'machine-learning',
    category: 'programming',
    title: 'Machine Learning',
    icon: '🧠',
    duration: '3 Months',
    level: 'Intermediate – Advanced',
    fee: 'Contact for fee',
    tagline: 'From algorithms to production-ready ML systems',
    description: 'A comprehensive Machine Learning course covering the full pipeline from data collection to model deployment. Learn both classical ML algorithms and modern deep learning approaches. Ideal for aspiring data scientists and ML engineers.',
    highlights: [
      'Full ML pipeline: data → model → deployment',
      'Classical algorithms: SVM, trees, ensembles',
      'Deep learning with neural networks',
      'Model interpretability and debugging',
      'Deployment with Flask and REST APIs',
      'Industry capstone project',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Foundations', items: ['Math for ML', 'Python Libraries', 'Data Preprocessing', 'Model Selection'] },
      { week: 'Month 2', topic: 'Algorithms', items: ['Regression & Classification', 'Ensemble Methods', 'Neural Networks', 'CNNs & RNNs'] },
      { week: 'Month 3', topic: 'Deployment & Projects', items: ['Model Evaluation', 'Flask Deployment', 'Capstone Project', 'Placement Prep'] },
    ],
    career: ['ML Engineer', 'Data Scientist', 'AI Researcher', 'Deep Learning Engineer'],
  },
  {
    slug: 'android-iot-training',
    category: 'programming',
    title: 'Android With IoT Training',
    icon: '📱',
    duration: '3 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Build smart apps that talk to the physical world',
    description: 'Learn to develop Android applications that communicate with IoT devices and sensors. This course combines Android development skills with IoT protocols, cloud connectivity, and hardware interfacing to build complete smart solutions.',
    highlights: [
      'Android app development with Kotlin',
      'IoT protocols: MQTT, CoAP, HTTP',
      'Bluetooth and Wi-Fi device communication',
      'Connecting apps to sensors and actuators',
      'Cloud integration with Firebase',
      'Smart home or automation project',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Android Development', items: ['Kotlin Basics', 'Android UI', 'Activities & Fragments', 'REST APIs'] },
      { week: 'Month 2', topic: 'IoT Fundamentals', items: ['IoT Architecture', 'MQTT Protocol', 'Raspberry Pi / Arduino', 'Sensor Integration'] },
      { week: 'Month 3', topic: 'Integration & Projects', items: ['Android ↔ IoT Bridge', 'Firebase Realtime DB', 'Smart App Project', 'Placement Prep'] },
    ],
    career: ['Android Developer', 'IoT Developer', 'Embedded Systems Engineer', 'Smart Systems Developer'],
  },
  {
    slug: 'iot-training',
    category: 'programming',
    title: 'IoT Training',
    icon: '🌐',
    duration: '2 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Connect the physical world to the internet',
    description: 'The Internet of Things is transforming industries. This course teaches you IoT fundamentals, hardware programming, networking protocols, and cloud connectivity. You will build real IoT projects using popular platforms.',
    highlights: [
      'IoT architecture and device ecosystem',
      'Arduino and Raspberry Pi programming',
      'Sensors, actuators, and hardware interfacing',
      'MQTT, HTTP, and CoAP protocols',
      'Cloud platforms: AWS IoT, Firebase',
      'End-to-end IoT project',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'IoT Basics & Hardware', items: ['IoT Overview', 'Arduino Programming', 'Sensors & Actuators', 'Circuit Design'] },
      { week: 'Week 4–5', topic: 'Networking & Protocols', items: ['Wi-Fi & Bluetooth', 'MQTT Protocol', 'REST APIs', 'Data Streaming'] },
      { week: 'Week 6–8', topic: 'Cloud & Projects', items: ['AWS IoT / Firebase', 'Data Visualization', 'IoT Dashboard', 'Final Project'] },
    ],
    career: ['IoT Developer', 'Embedded Engineer', 'Hardware Software Engineer', 'Solutions Architect'],
  },
  {
    slug: 'android-training',
    category: 'programming',
    title: 'Android Training',
    icon: '📲',
    duration: '3 Months',
    level: 'Beginner – Advanced',
    fee: 'Contact for fee',
    tagline: 'Build powerful Android apps from scratch',
    description: 'A complete Android development course covering Kotlin, Android SDK, UI design, data storage, REST APIs, and Google Play deployment. You will build multiple apps and leave with a portfolio ready to show employers.',
    highlights: [
      'Kotlin programming language',
      'Android Studio, Activities & Fragments',
      'Material Design UI components',
      'SQLite, Room, and SharedPreferences',
      'Retrofit for REST API integration',
      'Google Play Store deployment',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Android Fundamentals', items: ['Kotlin Basics', 'Android Studio Setup', 'UI Layouts', 'Activities & Intents'] },
      { week: 'Month 2', topic: 'Intermediate Android', items: ['Fragments & Navigation', 'RecyclerView', 'Data Storage', 'Background Services'] },
      { week: 'Month 3', topic: 'Advanced & Deployment', items: ['REST APIs with Retrofit', 'Firebase Integration', 'Play Store Publishing', 'Portfolio Project'] },
    ],
    career: ['Android Developer', 'Mobile Developer', 'App Developer', 'Software Engineer'],
  },
  {
    slug: 'iphone-training',
    category: 'programming',
    title: 'iPhone Training',
    icon: '🍎',
    duration: '3 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Build and publish iOS apps with Swift',
    description: 'Master iOS development using Swift and Xcode. This course covers UIKit, SwiftUI, data persistence, networking, and App Store submission. By the end you will have built real iOS apps and be ready for iOS developer roles.',
    highlights: [
      'Swift programming language fundamentals',
      'UIKit and SwiftUI frameworks',
      'Auto Layout and responsive design',
      'CoreData for local data storage',
      'URLSession and Alamofire for networking',
      'App Store Connect submission process',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Swift & iOS Basics', items: ['Swift Syntax', 'Xcode IDE', 'UIKit Basics', 'View Controllers'] },
      { week: 'Month 2', topic: 'Intermediate iOS', items: ['Table & Collection Views', 'Navigation', 'CoreData', 'Networking'] },
      { week: 'Month 3', topic: 'Advanced & Deployment', items: ['SwiftUI Introduction', 'Push Notifications', 'App Store Submission', 'Portfolio Apps'] },
    ],
    career: ['iOS Developer', 'Mobile Developer', 'Swift Developer', 'App Engineer'],
  },
  {
    slug: 'php-mysql-training',
    category: 'programming',
    title: 'PHP/MySQL Training',
    icon: '🐘',
    duration: '2 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Build dynamic websites with PHP and MySQL',
    description: 'PHP powers over 75% of all websites on the internet. Learn server-side web development with PHP and MySQL, build CRUD applications, handle forms, manage sessions, and develop a complete web application project.',
    highlights: [
      'PHP syntax, forms, sessions, cookies',
      'MySQL database design and queries',
      'PHP + MySQL CRUD applications',
      'Object-Oriented PHP and MVC concept',
      'Security best practices (SQL injection, XSS)',
      'Deploy your web app on a live server',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'PHP Basics', items: ['PHP Syntax', 'Forms & Validation', 'Sessions & Cookies', 'File Handling'] },
      { week: 'Week 4–5', topic: 'MySQL & Database', items: ['SQL Basics', 'CRUD Operations', 'Joins & Relationships', 'PHP + MySQL Integration'] },
      { week: 'Week 6–8', topic: 'OOP & Project', items: ['OOP PHP', 'MVC Pattern', 'Full Project', 'Deployment'] },
    ],
    career: ['PHP Developer', 'Web Developer', 'Backend Developer', 'Full Stack Developer'],
  },
  {
    slug: 'aspnet-training',
    category: 'programming',
    title: 'ASP.Net Training',
    icon: '🔷',
    duration: '3 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Enterprise-grade web development with Microsoft stack',
    description: 'Learn ASP.NET MVC and ASP.NET Core to build powerful, scalable web applications using C#. This course covers the full Microsoft stack including Entity Framework, Web APIs, SQL Server, and Azure deployment.',
    highlights: [
      'C# programming for web development',
      'ASP.NET MVC architecture',
      'Entity Framework for database access',
      'RESTful Web API development',
      'Authentication with Identity Framework',
      'Deployment to IIS and Azure',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'C# & .NET Basics', items: ['C# Syntax & OOP', '.NET Framework', 'ASP.NET MVC', 'Razor Views'] },
      { week: 'Month 2', topic: 'Database & APIs', items: ['Entity Framework', 'SQL Server', 'Web API', 'AJAX & jQuery'] },
      { week: 'Month 3', topic: 'Security & Deployment', items: ['Identity Auth', 'Role Management', 'Azure Deployment', 'Capstone Project'] },
    ],
    career: ['.NET Developer', 'C# Developer', 'ASP.NET Developer', 'Backend Engineer'],
  },
  {
    slug: 'java-training',
    category: 'programming',
    title: 'Java Training',
    icon: '☕',
    duration: '3 Months',
    level: 'Beginner – Advanced',
    fee: 'Contact for fee',
    tagline: 'Write once, run anywhere — master Java development',
    description: 'Java remains one of the most in-demand programming languages in the world. This course covers Core Java, object-oriented programming, Spring Boot for backend development, Hibernate for ORM, and REST API development.',
    highlights: [
      'Core Java: OOP, collections, generics, streams',
      'Java 8+ features: lambdas, streams, optionals',
      'Spring Boot for REST APIs',
      'Hibernate ORM with MySQL',
      'Spring Security and JWT authentication',
      'Maven build tool and Git version control',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Core Java', items: ['Java Basics & OOP', 'Collections Framework', 'Exception Handling', 'File I/O & Streams'] },
      { week: 'Month 2', topic: 'Spring Framework', items: ['Spring Core & DI', 'Spring Boot', 'REST API Development', 'Hibernate & JPA'] },
      { week: 'Month 3', topic: 'Advanced & Projects', items: ['Spring Security', 'Microservices Intro', 'Capstone Project', 'Placement Prep'] },
    ],
    career: ['Java Developer', 'Spring Developer', 'Backend Engineer', 'Software Engineer'],
  },
  {
    slug: 'odoo-training',
    category: 'programming',
    title: 'Odoo Training',
    icon: '🔧',
    duration: '2 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Master the world\'s leading open-source ERP',
    description: 'Odoo is the fastest-growing ERP platform. This course teaches you how to configure, customise, and develop modules for Odoo. Learn Python-based backend development, XML views, and business process automation.',
    highlights: [
      'Odoo architecture and module structure',
      'Python ORM and model development',
      'XML views, wizards, and reports',
      'Custom module development from scratch',
      'Odoo deployment and administration',
      'Real business workflow project',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'Odoo Basics', items: ['Odoo Setup', 'Module Structure', 'Models & Fields', 'Basic Views'] },
      { week: 'Week 4–6', topic: 'Custom Development', items: ['Python ORM', 'Form & List Views', 'Wizards', 'Business Logic'] },
      { week: 'Week 7–8', topic: 'Advanced & Projects', items: ['Reports & Workflows', 'Odoo Deployment', 'Custom Module Project', 'Review'] },
    ],
    career: ['Odoo Developer', 'ERP Developer', 'Functional Consultant', 'Python Developer'],
  },
  {
    slug: 'big-data-training',
    category: 'programming',
    title: 'Big Data Training',
    icon: '🗄️',
    duration: '3 Months',
    level: 'Advanced',
    fee: 'Contact for fee',
    tagline: 'Process and analyse massive datasets at scale',
    description: 'Big Data technologies are essential for modern enterprises. This course covers the full Big Data ecosystem including HDFS, MapReduce, Hive, Spark, and data pipeline design for processing datasets at petabyte scale.',
    highlights: [
      'Big Data concepts and the Hadoop ecosystem',
      'HDFS distributed file system',
      'MapReduce programming model',
      'Apache Hive for SQL on Big Data',
      'Apache Spark for real-time processing',
      'End-to-end data pipeline project',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Hadoop Ecosystem', items: ['Big Data Overview', 'HDFS Architecture', 'MapReduce', 'YARN'] },
      { week: 'Month 2', topic: 'Data Processing', items: ['Apache Hive', 'Apache Pig', 'HBase NoSQL', 'Sqoop & Flume'] },
      { week: 'Month 3', topic: 'Spark & Projects', items: ['Apache Spark Core', 'Spark SQL', 'Spark Streaming', 'Capstone Project'] },
    ],
    career: ['Big Data Engineer', 'Data Engineer', 'Hadoop Developer', 'Data Architect'],
  },
  {
    slug: 'hadoop-training',
    category: 'programming',
    title: 'Hadoop Training',
    icon: '🐘',
    duration: '2 Months',
    level: 'Advanced',
    fee: 'Contact for fee',
    tagline: 'The backbone of Big Data — master Hadoop',
    description: 'Hadoop is the foundation of Big Data processing. Learn to set up and manage Hadoop clusters, work with HDFS, write MapReduce jobs, and use the wider Hadoop ecosystem tools for real-world data engineering tasks.',
    highlights: [
      'Hadoop architecture and cluster setup',
      'HDFS operations and data management',
      'MapReduce job development in Java',
      'Apache Hive, Pig, and HBase',
      'Data ingestion with Sqoop and Flume',
      'Hadoop administration and monitoring',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'Hadoop Core', items: ['HDFS', 'MapReduce', 'YARN', 'Cluster Setup'] },
      { week: 'Week 4–6', topic: 'Ecosystem Tools', items: ['Hive & HiveQL', 'Pig Latin', 'HBase', 'Sqoop'] },
      { week: 'Week 7–8', topic: 'Projects', items: ['Data Pipeline', 'Cluster Admin', 'Capstone Project', 'Review'] },
    ],
    career: ['Hadoop Developer', 'Big Data Engineer', 'Data Engineer', 'Cluster Admin'],
  },
  {
    slug: 'magento-training',
    category: 'programming',
    title: 'Magento Training',
    icon: '🛒',
    duration: '2 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Build powerful e-commerce stores with Magento',
    description: 'Magento is the enterprise e-commerce platform trusted by thousands of global brands. Learn to develop Magento themes, create custom modules, configure payment gateways, and manage a full online store.',
    highlights: [
      'Magento 2 architecture and module system',
      'Theme development with LESS/CSS',
      'Custom module development in PHP',
      'Product, category, and inventory management',
      'Payment gateway and shipping integration',
      'Performance optimisation and deployment',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'Magento Basics', items: ['Installation & Setup', 'Admin Panel', 'Themes & Layouts', 'Product Management'] },
      { week: 'Week 4–6', topic: 'Development', items: ['Custom Modules', 'Events & Observers', 'Payment Integration', 'Custom APIs'] },
      { week: 'Week 7–8', topic: 'Project', items: ['Full Store Build', 'Performance Tuning', 'Deployment', 'Review'] },
    ],
    career: ['Magento Developer', 'E-commerce Developer', 'PHP Developer', 'Solutions Developer'],
  },
  {
    slug: 'wordpress-training',
    category: 'programming',
    title: 'WordPress Training',
    icon: '📝',
    duration: '1.5 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'The world\'s most popular CMS — inside and out',
    description: 'WordPress powers 43% of all websites on the internet. Learn to build, customise, and extend WordPress websites for clients and businesses. Covers theme development, plugin creation, WooCommerce, and SEO.',
    highlights: [
      'WordPress installation and configuration',
      'Custom theme development with PHP',
      'Plugin development from scratch',
      'WooCommerce store setup',
      'SEO with Yoast and performance optimisation',
      'Deploying WordPress on live hosting',
    ],
    curriculum: [
      { week: 'Week 1–2', topic: 'WordPress Basics', items: ['Installation', 'Themes & Plugins', 'Page Builders', 'Content Management'] },
      { week: 'Week 3–4', topic: 'Theme Development', items: ['PHP Templates', 'Custom Post Types', 'Hooks & Filters', 'Child Themes'] },
      { week: 'Week 5–6', topic: 'Plugins & Projects', items: ['Plugin Development', 'WooCommerce', 'SEO Setup', 'Live Deployment'] },
    ],
    career: ['WordPress Developer', 'Web Developer', 'CMS Developer', 'Freelance Developer'],
  },
  {
    slug: 'codeigniter-training',
    category: 'programming',
    title: 'CodeIgniter Training',
    icon: '🔥',
    duration: '2 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Fast and lightweight PHP MVC framework',
    description: 'CodeIgniter is a lightweight, high-performance PHP MVC framework used by thousands of developers worldwide. Learn to build secure, scalable web applications rapidly using CodeIgniter\'s simple but powerful architecture.',
    highlights: [
      'MVC architecture in CodeIgniter',
      'Routing, controllers, models, and views',
      'CodeIgniter database query builder',
      'Form validation and security',
      'Libraries, helpers, and hooks',
      'Full CRUD web application project',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'CI Fundamentals', items: ['MVC Architecture', 'Routing', 'Controllers & Views', 'Models & DB'] },
      { week: 'Week 4–5', topic: 'Intermediate Features', items: ['Form Validation', 'Sessions & Auth', 'Libraries', 'RESTful APIs'] },
      { week: 'Week 6–8', topic: 'Project', items: ['CRUD App Build', 'Security Hardening', 'Deployment', 'Review'] },
    ],
    career: ['PHP Developer', 'CodeIgniter Developer', 'Backend Developer', 'Web Developer'],
  },
  {
    slug: 'drupal-training',
    category: 'programming',
    title: 'Drupal Training',
    icon: '💧',
    duration: '2 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Enterprise CMS used by governments and corporations',
    description: 'Drupal is the CMS of choice for complex enterprise websites, government portals, and high-traffic platforms. Learn Drupal site building, custom module development, Twig theming, and Drupal APIs.',
    highlights: [
      'Drupal site configuration and content types',
      'Views, blocks, and taxonomy',
      'Custom module development in PHP',
      'Twig templating for theming',
      'Drupal REST API and JSON:API',
      'Deployment and performance tuning',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'Drupal Basics', items: ['Installation', 'Content Types', 'Views & Blocks', 'Taxonomy'] },
      { week: 'Week 4–6', topic: 'Development', items: ['Module Development', 'Twig Theming', 'Form API', 'Drupal Services'] },
      { week: 'Week 7–8', topic: 'Project', items: ['Enterprise Site Build', 'REST API', 'Deployment', 'Review'] },
    ],
    career: ['Drupal Developer', 'CMS Developer', 'Backend Developer', 'PHP Developer'],
  },
  {
    slug: 'joomla-training',
    category: 'programming',
    title: 'Joomla Training',
    icon: '🌀',
    duration: '1.5 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Flexible CMS for every kind of website',
    description: 'Joomla is a powerful, flexible CMS used by millions of websites worldwide. This course covers Joomla site building, extension development, template customisation, and best practices for running Joomla-based projects.',
    highlights: [
      'Joomla installation and administration',
      'Articles, categories, and menus',
      'Template customisation and theming',
      'Custom component and module development',
      'ACL user management',
      'SEO and performance optimisation',
    ],
    curriculum: [
      { week: 'Week 1–2', topic: 'Joomla Basics', items: ['Installation', 'Admin Panel', 'Content Management', 'Extensions'] },
      { week: 'Week 3–4', topic: 'Customisation', items: ['Template Development', 'Module Building', 'Component Basics', 'ACL'] },
      { week: 'Week 5–6', topic: 'Project', items: ['Full Site Build', 'SEO Setup', 'Deployment', 'Review'] },
    ],
    career: ['Joomla Developer', 'CMS Developer', 'Web Developer', 'PHP Developer'],
  },
  {
    slug: 'silverlight-training',
    category: 'programming',
    title: 'Silverlight Training',
    icon: '✨',
    duration: '1.5 Months',
    level: 'Intermediate',
    fee: 'Contact for fee',
    tagline: 'Rich Internet Application development with XAML',
    description: 'Learn to build rich internet applications using Microsoft Silverlight and XAML. This course covers the Silverlight runtime, XAML markup, data binding, media streaming, and building cross-browser interactive applications.',
    highlights: [
      'Silverlight architecture and XAML basics',
      'Data binding and controls',
      'Animation and storyboards',
      'Media streaming and video players',
      'WCF service integration',
      'Cross-browser deployment',
    ],
    curriculum: [
      { week: 'Week 1–2', topic: 'Silverlight Basics', items: ['XAML Markup', 'Controls & Layout', 'Data Binding', 'Styles'] },
      { week: 'Week 3–4', topic: 'Advanced Features', items: ['Animation', 'Media Streaming', 'WCF Services', 'Navigation'] },
      { week: 'Week 5–6', topic: 'Project', items: ['RIA Application', 'Deployment', 'Review'] },
    ],
    career: ['.NET Developer', 'Silverlight Developer', 'RIA Developer', 'XAML Developer'],
  },
  {
    slug: 'c-language-training',
    category: 'programming',
    title: 'C Language Training',
    icon: '💻',
    duration: '2 Months',
    level: 'Beginner',
    fee: 'Contact for fee',
    tagline: 'The foundation of all modern programming',
    description: 'C is the foundation upon which modern computing is built. Learning C gives you deep understanding of how programs work at a low level. This course covers C syntax, pointers, memory management, data structures, and file handling.',
    highlights: [
      'C syntax, operators, and control flow',
      'Pointers and memory management',
      'Arrays, strings, and structures',
      'Data structures: linked lists, stacks, queues',
      'File I/O operations',
      'Algorithm and problem-solving practice',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'C Basics', items: ['C Syntax', 'Control Flow', 'Functions', 'Arrays & Strings'] },
      { week: 'Week 4–5', topic: 'Pointers & Memory', items: ['Pointers', 'Dynamic Memory', 'Structures & Unions', 'File I/O'] },
      { week: 'Week 6–8', topic: 'Data Structures & Projects', items: ['Linked Lists', 'Stacks & Queues', 'Algorithm Problems', 'Final Project'] },
    ],
    career: ['C Developer', 'Systems Programmer', 'Embedded Developer', 'Foundation for any language'],
  },
  {
    slug: 'cpp-language-training',
    category: 'programming',
    title: 'C++ Language Training',
    icon: '⚙️',
    duration: '2 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Object-oriented power meets system-level control',
    description: 'C++ extends C with object-oriented features and is widely used in game development, system software, and high-performance applications. This course covers C++ OOP, STL, templates, and modern C++11/14/17 features.',
    highlights: [
      'C++ OOP: classes, inheritance, polymorphism',
      'Standard Template Library (STL)',
      'Templates and generic programming',
      'Modern C++11/14/17 features',
      'Memory management with smart pointers',
      'Data structures and algorithm practice',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'C++ Basics & OOP', items: ['C++ Syntax', 'Classes & Objects', 'Inheritance', 'Polymorphism'] },
      { week: 'Week 4–5', topic: 'STL & Templates', items: ['STL Containers', 'Iterators & Algorithms', 'Templates', 'Exception Handling'] },
      { week: 'Week 6–8', topic: 'Modern C++ & Projects', items: ['C++11 Features', 'Smart Pointers', 'DS&A Practice', 'Final Project'] },
    ],
    career: ['C++ Developer', 'Game Developer', 'Systems Engineer', 'Competitive Programmer'],
  },
  {
    slug: 'software-testing',
    category: 'programming',
    title: 'Software Testing',
    icon: '🧪',
    duration: '2 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Ensure quality — become a sought-after QA engineer',
    description: 'Software Testing is a critical and well-paid career in IT. This course covers manual testing fundamentals, automation testing with Selenium, test management with JIRA, and API testing with Postman.',
    highlights: [
      'Software testing life cycle (STLC)',
      'Test case design and execution',
      'Bug reporting and tracking with JIRA',
      'Selenium WebDriver automation',
      'TestNG framework for automation',
      'API testing with Postman',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'Manual Testing', items: ['SDLC & STLC', 'Test Case Design', 'Bug Reporting', 'JIRA Tool'] },
      { week: 'Week 4–6', topic: 'Automation Testing', items: ['Selenium WebDriver', 'TestNG', 'Page Object Model', 'Test Reports'] },
      { week: 'Week 7–8', topic: 'Advanced & Projects', items: ['API Testing (Postman)', 'Performance Testing', 'Test Project', 'Placement Prep'] },
    ],
    career: ['QA Engineer', 'Test Automation Engineer', 'SDET', 'Software Tester'],
  },
  {
    slug: 'digital-marketing-prog',
    category: 'programming',
    title: 'Digital Marketing',
    icon: '📣',
    duration: '2 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Grow any business online with digital marketing',
    description: 'Digital marketing skills are in high demand across all industries. This comprehensive course covers SEO, Google Ads, social media marketing, email marketing, content strategy, and analytics — everything you need to market a business online.',
    highlights: [
      'SEO fundamentals and keyword research',
      'Google Ads (Search, Display, Shopping)',
      'Social media marketing on all platforms',
      'Email marketing and automation',
      'Content creation and strategy',
      'Google Analytics and ROI tracking',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'SEO & Content', items: ['Keyword Research', 'On-Page SEO', 'Off-Page SEO', 'Content Marketing'] },
      { week: 'Week 4–5', topic: 'Paid Advertising', items: ['Google Ads', 'Facebook Ads', 'Instagram Ads', 'Budget Management'] },
      { week: 'Week 6–8', topic: 'Analytics & Projects', items: ['Google Analytics', 'Email Marketing', 'Social Media Strategy', 'Campaign Project'] },
    ],
    career: ['Digital Marketing Executive', 'SEO Analyst', 'PPC Manager', 'Social Media Manager'],
  },

  /* ─── NON-PROGRAMMING COURSES ─── */
  {
    slug: 'web-design-training',
    category: 'non-programming',
    title: 'Web Design Training',
    icon: '🎨',
    duration: '2 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Design beautiful, responsive websites',
    description: 'Web design is the creative and technical skill of building visually appealing, user-friendly websites. This course covers HTML5, CSS3, responsive design, JavaScript interactivity, and industry-standard design tools.',
    highlights: [
      'HTML5 semantic markup and document structure',
      'CSS3: Flexbox, Grid, animations',
      'Responsive and mobile-first design',
      'JavaScript and jQuery for interactivity',
      'Adobe Photoshop for web graphics',
      'Build and deploy a complete website',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'HTML5 & CSS3', items: ['HTML5 Structure', 'CSS3 Styling', 'Flexbox & Grid', 'Responsive Design'] },
      { week: 'Week 4–5', topic: 'JavaScript & jQuery', items: ['JS Basics', 'DOM Manipulation', 'jQuery Effects', 'Form Validation'] },
      { week: 'Week 6–8', topic: 'Design Tools & Projects', items: ['Photoshop for Web', 'UI/UX Principles', 'Portfolio Website', 'Deployment'] },
    ],
    career: ['Web Designer', 'UI Developer', 'Front-End Developer', 'Freelance Web Designer'],
  },
  {
    slug: 'graphics-design-training',
    category: 'non-programming',
    title: 'Graphics Design Training',
    icon: '🖼️',
    duration: '2.5 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Create stunning visuals with industry tools',
    description: 'Graphic design is the art of visual communication. This course teaches you to create logos, banners, brochures, social media graphics, and more using Adobe Photoshop, Illustrator, and CorelDRAW.',
    highlights: [
      'Adobe Photoshop: photo editing and web graphics',
      'Adobe Illustrator: vector art and logo design',
      'CorelDRAW: print and marketing materials',
      'Adobe Flash: animation basics',
      'Typography, colour theory, and layout principles',
      'Build a complete design portfolio',
    ],
    curriculum: [
      { week: 'Month 1', topic: 'Photoshop & Design Basics', items: ['Photoshop Tools', 'Photo Editing', 'Digital Painting', 'Web Graphics'] },
      { week: 'Month 2', topic: 'Illustrator & CorelDRAW', items: ['Vector Graphics', 'Logo Design', 'CorelDRAW Layouts', 'Print Design'] },
      { week: 'Final Weeks', topic: 'Animation & Portfolio', items: ['Adobe Flash Basics', 'Animation Principles', 'Portfolio Creation', 'Review'] },
    ],
    career: ['Graphic Designer', 'Visual Designer', 'Brand Identity Designer', 'Freelance Designer'],
  },
  {
    slug: 'seo-training',
    category: 'non-programming',
    title: 'SEO Training',
    icon: '🔍',
    duration: '1.5 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Rank higher on Google and drive organic traffic',
    description: 'Search Engine Optimisation (SEO) is one of the most valuable digital skills today. Learn to optimise websites for Google search, conduct keyword research, build backlinks, and track performance — all without paid advertising.',
    highlights: [
      'On-page SEO: keywords, meta tags, content',
      'Technical SEO: site speed, mobile, schema',
      'Off-page SEO: link building and outreach',
      'Local SEO for businesses',
      'SEO tools: SEMrush, Ahrefs, GSC',
      'Real website SEO audit project',
    ],
    curriculum: [
      { week: 'Week 1–2', topic: 'SEO Fundamentals', items: ['How Google Works', 'Keyword Research', 'On-Page SEO', 'Content Optimisation'] },
      { week: 'Week 3–4', topic: 'Technical & Off-Page', items: ['Technical SEO', 'Site Audit', 'Link Building', 'Local SEO'] },
      { week: 'Week 5–6', topic: 'Tools & Projects', items: ['SEMrush & Ahrefs', 'Google Search Console', 'SEO Audit Project', 'Reporting'] },
    ],
    career: ['SEO Analyst', 'SEO Executive', 'Digital Marketer', 'Content Strategist'],
  },
  {
    slug: 'digital-marketing-nonprog',
    category: 'non-programming',
    title: 'Digital Marketing',
    icon: '📣',
    duration: '2 Months',
    level: 'Beginner – Advanced',
    fee: 'Contact for fee',
    tagline: 'Full-stack digital marketing for the modern business',
    description: 'Master all channels of digital marketing including SEO, paid ads, social media, email, and analytics. Learn to plan, execute, and measure digital campaigns that deliver real business results.',
    highlights: [
      'Digital marketing strategy and planning',
      'Google Ads: Search, Display, YouTube',
      'Social media marketing (Facebook, Instagram, LinkedIn)',
      'Email marketing campaigns and automation',
      'Google Analytics 4 and data-driven decisions',
      'Live campaign project',
    ],
    curriculum: [
      { week: 'Week 1–3', topic: 'Foundations & SEO', items: ['Digital Marketing Overview', 'SEO Basics', 'Content Marketing', 'Social Media Strategy'] },
      { week: 'Week 4–5', topic: 'Paid Ads', items: ['Google Ads Setup', 'Facebook & Instagram Ads', 'Retargeting', 'Ad Copywriting'] },
      { week: 'Week 6–8', topic: 'Analytics & Projects', items: ['GA4 Analytics', 'Email Automation', 'Campaign Management', 'Final Project'] },
    ],
    career: ['Digital Marketing Executive', 'PPC Manager', 'Social Media Manager', 'Marketing Analyst'],
  },
  {
    slug: 'business-development-training',
    category: 'non-programming',
    title: 'Business Development Training',
    icon: '📈',
    duration: '1.5 Months',
    level: 'Beginner – Intermediate',
    fee: 'Contact for fee',
    tagline: 'Drive growth and close deals in the IT industry',
    description: 'Business Development is the engine of any IT company\'s growth. This course teaches you sales strategy, lead generation, proposal writing, client management, and IT-specific business development skills that open doors to high-paying roles.',
    highlights: [
      'Sales process and IT business development',
      'Lead generation via LinkedIn and email',
      'Proposal writing and presentation skills',
      'CRM tools like HubSpot and Salesforce',
      'Client relationship management',
      'Pricing, negotiation, and closing deals',
    ],
    curriculum: [
      { week: 'Week 1–2', topic: 'BD Fundamentals', items: ['Sales Process', 'Lead Generation', 'Cold Outreach', 'LinkedIn Strategy'] },
      { week: 'Week 3–4', topic: 'Proposals & CRM', items: ['Proposal Writing', 'Presentations', 'CRM Tools', 'Pipeline Management'] },
      { week: 'Week 5–6', topic: 'Closing & Projects', items: ['Negotiation', 'Client Management', 'BD Strategy Project', 'Review'] },
    ],
    career: ['Business Development Executive', 'Sales Manager', 'IT Sales Consultant', 'Account Manager'],
  },

  /* ─── SPECIAL TRAINING ─── */
  {
    slug: 'free-training',
    category: 'special',
    title: 'Free Training Program',
    icon: '🎁',
    duration: 'Varies by course',
    level: 'Beginner',
    fee: 'FREE',
    tagline: 'Start your IT journey — at zero cost',
    description: 'We believe that cost should never be a barrier to starting a career in IT. Our Free Training Program provides introductory sessions in selected technologies, giving you a hands-on taste of programming and IT skills before you commit to a full course.',
    highlights: [
      'Introductory sessions in selected technologies',
      'Access to course materials and notes',
      'Guidance from experienced trainers',
      'Get a feel for the course before enrolling',
      'Certificate of participation',
      'No registration fee — absolutely free',
    ],
    curriculum: [
      { week: 'Demo Sessions', topic: 'Technology Introduction', items: ['Overview of the technology', 'Why learn it and career scope', 'Hands-on demo project', 'Q&A with trainers'] },
    ],
    career: ['Entry point for any IT career path'],
  },
  {
    slug: 'weekend-training',
    category: 'special',
    title: 'Weekend Training Program',
    icon: '📅',
    duration: 'Same as regular — spread over weekends',
    level: 'All Levels',
    fee: 'Contact for fee',
    tagline: 'Learn on weekends, grow your career all week',
    description: 'Our Weekend Training Program gives working professionals and students the flexibility to learn without disrupting their week. Classes are held on Saturdays and Sundays, covering the full curriculum at the same pace as regular batches.',
    highlights: [
      'PHP Weekend Training',
      '.Net Weekend Training',
      'Java Weekend Training',
      'Android Weekend Training',
      'Morning and evening batch options',
      'Same curriculum, certificate, and placement support',
    ],
    curriculum: [
      { week: 'Every Saturday & Sunday', topic: 'Full Course Curriculum', items: ['Same content as weekday batches', 'Morning batch: 9 AM – 1 PM', 'Evening batch: 2 PM – 6 PM', 'Doubt clearing sessions included'] },
    ],
    career: ['All IT career paths — same as full course'],
  },
  {
    slug: 'corporate-training',
    category: 'special',
    title: 'Corporate Training Program',
    icon: '🏢',
    duration: 'Custom — typically 1–3 Months',
    level: 'All Levels',
    fee: 'Group pricing — Contact us',
    tagline: 'Upskill your entire team with customised training',
    description: 'Our Corporate Training Program is designed for companies that want to train their teams on specific technologies, tools, or workflows. We build a fully customised training plan around your technology stack and business goals, delivered on-site or online.',
    highlights: [
      'Curriculum tailored to your tech stack',
      'On-site or online delivery',
      'Group discounts for 5+ employees',
      'Progress reports and assessments',
      'Post-training support and Q&A',
      'Certificate for all participants',
    ],
    curriculum: [
      { week: 'Customised', topic: 'Your Technology Stack', items: ['Initial assessment of team skill levels', 'Custom curriculum development', 'Live training sessions', 'Assessments and progress tracking', 'Post-training support'] },
    ],
    career: ['Applicable to all corporate IT roles'],
  },
];

// Helper to find by slug
export function getCourseBySlug(slug) {
  return allCourses.find(c => c.slug === slug) || null;
}

// Group by category
export const programmingCourses = allCourses.filter(c => c.category === 'programming');
export const nonProgrammingCourses = allCourses.filter(c => c.category === 'non-programming');
export const specialCourses = allCourses.filter(c => c.category === 'special');

// Slug helpers
export function toSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
